// TopicCard.tsx
import { QuizTopic, StudentWithGrade } from "./types";
import { BookOpen, CheckCircle, Circle, Clock, Award } from "lucide-react";

interface TopicCardProps {
  topic: QuizTopic;
  isSelected: boolean;
  onSelect: () => void;
  onStart: () => void;
  studentGrade?: StudentWithGrade;
  onViewCertificate?: () => void;
}

export const TopicCard = ({ 
  topic, 
  isSelected, 
  onSelect, 
  onStart,
  studentGrade,
  onViewCertificate
}: TopicCardProps) => {
  const getStatusBadge = () => {
    if (!studentGrade) {
      return (
        <span className="text-xs text-gray-500 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Not attempted
        </span>
      );
    }
    
    if (studentGrade.passed) {
      return (
        <span className="text-xs font-medium text-green-600 bg-green-50 px-2.5 py-0.5 rounded-full flex items-center gap-1.5">
          <CheckCircle className="w-3.5 h-3.5" />
          {studentGrade.score}% - Passed
        </span>
      );
    }
    
    return (
      <span className="text-xs font-medium text-red-600 bg-red-50 px-2.5 py-0.5 rounded-full flex items-center gap-1.5">
        <Circle className="w-3.5 h-3.5" />
        {studentGrade.score}% - Failed
      </span>
    );
  };

  return (
    <div 
      className={`group relative bg-white rounded-2xl shadow-sm border-2 transition-all duration-200 overflow-hidden cursor-pointer ${
        isSelected ? 'border-indigo-500 shadow-md' : 'border-gray-100 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1.5">
              {topic.title}
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              {topic.questions?.length || 0} questions • {topic.passingPercentage}% to pass
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br flex-shrink-0" style={{ 
            background: `linear-gradient(135deg, ${topic.color}20, ${topic.color}40)` 
          }}>
            <BookOpen className="w-5 h-5 m-2.5" style={{ color: topic.color }} />
          </div>
        </div>
        
        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2">
          {topic.description}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            {studentGrade && (
              <span className="text-xs text-gray-400">
                {new Date(studentGrade.completedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* View Certificate Button - Only show if passed */}
            {studentGrade?.passed && onViewCertificate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewCertificate();
                }}
                className="p-2 rounded-xl text-indigo-600 hover:bg-indigo-50 transition"
                title="View Certificate"
              >
                <Award className="w-4 h-4" />
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStart();
              }}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition whitespace-nowrap shadow-sm hover:shadow-md"
            >
              {studentGrade ? 'Retake Quiz' : 'Start Quiz'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute inset-0 border-2 border-indigo-500 rounded-2xl pointer-events-none" />
      )}
    </div>
  );
};